import * as L from 'leaflet';

export type LegendMode = 'flat' | 'grouped';

export interface DynamicLegendOptions extends L.ControlOptions {
  mode?: LegendMode;
}

export class DynamicLegend extends L.Control {
  private _container: HTMLElement | undefined;
  
  // Data structures for both modes
  private _flatItems: Map<string, string> = new Map(); // label -> html
  private _groupedItems: Map<string, Map<string, string>> = new Map(); // group -> label -> html
  private _groupVisibility: Map<string, boolean> = new Map(); // group -> visibility state
  private _groupCollapsed: Map<string, boolean> = new Map(); // group -> collapsed state
  
  public mode: LegendMode;
  public onGroupToggle?: (groupName: string, isVisible: boolean) => void;

  constructor(options?: DynamicLegendOptions) {
    super({ ...options, position: 'bottomright' });
    this.mode = options?.mode || 'flat';
  }

  onAdd(map: L.Map): HTMLElement {
    this._container = L.DomUtil.create('div', 'leaflet-legend-control overflow-auto');
    L.DomEvent.disableClickPropagation(this._container);

    // Calculate max-height based on map container height
    const mapContainer = map.getContainer();
    const mapHeight = mapContainer.offsetHeight;
    const maxLegendHeight = Math.max(200, mapHeight - 40); // At least 200px, or map height minus padding

    // Apply max-height (no Bootstrap utility for calculated max-height)
    this._container.style.maxHeight = `${maxLegendHeight}px`;

    // Apply additional classes for flat mode
    if (this.mode === 'flat') {
      this._container.classList.add('bg-white', 'p-2', 'border', 'rounded');
    }

    // Use event delegation for checkboxes to avoid rebinding on high-frequency updates
    L.DomEvent.on(this._container, 'change', (e: Event) => {
      const target = e.target as HTMLInputElement;
      if (target && target.classList.contains('legend-group-toggle')) {
        const groupName = target.getAttribute('data-group');
        if (groupName) {
          // Update the stored visibility state
          this._groupVisibility.set(groupName, target.checked);
          if (this.onGroupToggle) {
            this.onGroupToggle(groupName, target.checked);
          }
        }
      }
    });

    // Handle collapse toggle clicks on header
    L.DomEvent.on(this._container, 'click', (e: Event) => {
      const target = e.target as HTMLElement;
      
      // Don't trigger collapse if clicking on checkbox or its label
      if (target.classList.contains('legend-group-toggle') || 
          target.classList.contains('form-check-input') ||
          target.closest('label.legend-checkbox-label')) {
        return;
      }
      
      // Find the header element
      const header = target.closest('.legend-header');
      if (header) {
        const groupName = header.getAttribute('data-group');
        if (groupName) {
          const contentId = `legend-content-${groupName.replace(/\s+/g, '-')}`;
          const contentElement = this._container?.querySelector(`#${contentId}`) as HTMLElement;
          const icon = header.querySelector('.collapse-icon');
          
          if (contentElement) {
            const isCollapsed = contentElement.style.display === 'none';
            contentElement.style.display = isCollapsed ? 'block' : 'none';
            this._groupCollapsed.set(groupName, !isCollapsed);
            
            // Update icon
            if (icon) {
              icon.textContent = isCollapsed ? '−' : '+';
            }
          }
        }
      }
    });

    this.update();
    return this._container;
  }

  // Modified to accept an optional groupName and initial visibility
  addItem(label: string, symbolHtml: string, groupName?: string, initiallyVisible: boolean = true) {
    if (this.mode === 'grouped' && groupName) {
      if (!this._groupedItems.has(groupName)) {
        this._groupedItems.set(groupName, new Map());
        // Set initial visibility only if this is the first time we're seeing this group
        if (!this._groupVisibility.has(groupName)) {
          this._groupVisibility.set(groupName, initiallyVisible);
        }
        // Set initial collapsed state (default to expanded)
        if (!this._groupCollapsed.has(groupName)) {
          this._groupCollapsed.set(groupName, false);
        }
      }
      this._groupedItems.get(groupName)!.set(label, symbolHtml);
    } else {
      this._flatItems.set(label, symbolHtml);
    }
    this.update();
  }

  removeItem(label: string, groupName?: string) {
    if (this.mode === 'grouped' && groupName) {
      this._groupedItems.get(groupName)?.delete(label);
    } else {
      this._flatItems.delete(label);
    }
    this.update();
  }

  private update() {
    if (!this._container) return;

    const isEmpty = this.mode === 'grouped' ? 
      (this._groupedItems.size === 0 && this._flatItems.size === 0) : 
      this._flatItems.size === 0;

    if (isEmpty) {
      this._container.style.display = 'none';
      return;
    }

    this._container.style.display = 'block';
    let html = '';

    if (this.mode === 'grouped') {
      // Grouped Mode DOM Structure - Render groups first
      this._groupedItems.forEach((items, groupName) => {
        const isVisible = this._groupVisibility.get(groupName) ?? true;
        const isCollapsed = this._groupCollapsed.get(groupName) ?? false;
        const checkedAttr = isVisible ? 'checked' : '';
        const displayStyle = isCollapsed ? 'display: none;' : '';
        const collapseIcon = isCollapsed ? '+' : '−';
        const contentId = `legend-content-${groupName.replace(/\s+/g, '-')}`;
        
        html += `
          <div class="legend-group bg-white border rounded shadow-sm mb-2" style="min-width: 220px;">
            <div class="legend-header border-bottom bg-primary px-2 py-1 d-flex justify-content-between align-items-center rounded" data-group="${groupName}" style="cursor: pointer;">
              <div class="d-flex align-items-center gap-1">
                <span class="collapse-icon" style="font-size: 14px; min-width: 16px; font-weight: bold;">${collapseIcon}</span>
                <span class="small text-white fw-bold">${groupName}</span>
              </div>
              <label class="legend-checkbox-label small d-flex align-items-center gap-1 mb-0 text-secondary" style="cursor: pointer;" onclick="event.stopPropagation();">
                <input type="checkbox" ${checkedAttr} class="legend-group-toggle form-check-input mt-0" data-group="${groupName}" style="cursor: pointer;"> <span class="text-dark">Show</span>
              </label>
            </div>
            <div id="${contentId}" class="legend-items p-2" style="${displayStyle}">
        `;

        items.forEach((symbol, label) => {
          html += this.generateItemHtml(label, symbol);
        });

        html += `</div></div>`;
      });

      // Render individual (flat) items below groups if any exist
      if (this._flatItems.size > 0) {
        const flatGroupName = 'Individual Items';
        // Initialize collapsed state if not already set
        if (!this._groupCollapsed.has(flatGroupName)) {
          this._groupCollapsed.set(flatGroupName, false);
        }
        const isCollapsed = this._groupCollapsed.get(flatGroupName) ?? false;
        const displayStyle = isCollapsed ? 'display: none;' : '';
        const collapseIcon = isCollapsed ? '+' : '−';
        const contentId = `legend-content-${flatGroupName.replace(/\s+/g, '-')}`;
        
        html += `
          <div class="legend-group bg-white border rounded shadow-sm mb-2" style="min-width: 220px;">
            <div class="legend-header border-bottom bg-primary px-2 py-1 d-flex justify-content-between align-items-center rounded" data-group="${flatGroupName}" style="cursor: pointer;">
              <div class="d-flex align-items-center gap-1">
                <span class="collapse-icon" style="font-size: 14px; min-width: 16px; font-weight: bold;">${collapseIcon}</span>
                <span class="small text-white fw-bold">${flatGroupName}</span>
              </div>
            </div>
            <div id="${contentId}" class="legend-items p-2" style="${displayStyle}">
        `;

        this._flatItems.forEach((symbol, label) => {
          html += this.generateItemHtml(label, symbol);
        });

        html += `</div></div>`;
      }
    } else {
      // Flat Mode DOM Structure (Original functionality)
      html = '<strong class="d-block mb-2 text-dark">Map Legend</strong>';
      this._flatItems.forEach((symbol, label) => {
        html += this.generateItemHtml(label, symbol);
      });
    }

    this._container.innerHTML = html;
  }

  clear() {
    this._flatItems.clear();
    this._groupedItems.clear();
    this.update();
  }

  private generateItemHtml(label: string, symbol: string): string {
    return `
      <div class="legend-item d-flex align-items-center gap-2 mb-2 text-dark small">
        <span class="d-flex align-items-center justify-content-center" style="width: 14px; height: 14px;">
          ${symbol}
        </span>
        <span>${label}</span>
      </div>
    `;
  }
}